<template>
  <div class="app-shell home-page tool-page">
    <div class="app-shell__main">
      <AppHeader />

      <main class="home-main tool-main">
      <section class="hero-section tool-hero-section tool-page-hero">
        <div class="tool-hero-grid">
          <div class="tool-hero-copy">
            <span class="tool-eyebrow">Link Clean</span>
            <h1 class="hero-title">
              链接去水印
            </h1>
            <p class="hero-description">
              输入分享链接，快速提取无水印视频、封面和音频素材。
            </p>
            <div class="tool-copy-pills">
              <span>支持视频、封面和音频资源</span>
              <span>解析后可直接下载与复制链接</span>
              <span>适合二创整理与本地保存</span>
            </div>
            <div class="tool-hero-summary">
              <article class="tool-hero-stat">
                <strong>分享链接</strong>
                <span>支持直接粘贴带文案的分享内容，快速识别可处理素材。</span>
              </article>
              <article class="tool-hero-stat">
                <strong>结果一页整理</strong>
                <span>解析完成后集中查看视频、封面与音频，不用反复切换页面。</span>
              </article>
              <article class="tool-hero-stat">
                <strong>更适合整理保存</strong>
                <span>适合做本地备份、剪辑前整理和二创素材收集。</span>
              </article>
            </div>
          </div>

          <div class="tool-workbench tool-workbench--link">
          <!-- <div class="tool-stage-note">
            <div class="tool-stage-note__title">解析工作台</div>
            <p>复制平台分享链接后直接粘贴，系统会自动整理出当前可下载的核心素材。</p>
          </div> -->
          <div class="video-form-container">
            <div class="error-message" v-if="errorMessage">
              {{ errorMessage }}
            </div>
            <div class="error-placeholder" v-else></div>

            <form @submit.prevent="handleSubmit" class="video-form">
              <div class="input-container">
                <div class="input-wrapper">
                  <input ref="urlInput" v-model="videoUrl" type="text" placeholder="请输入视频链接（可带文字内容的链接）"
                    class="url-input" :disabled="isLoading" />
                  <button v-if="videoUrl.length > 0" type="button" @click="clearUrl" class="clear-button">
                    <svg class="clear-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>

              <button type="submit" :disabled="isDisabled" class="submit-button">
                <svg v-if="isLoading" class="loading-icon" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-dasharray="31.416" stroke-dashoffset="31.416">
                    <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416"
                      repeatCount="indefinite" />
                    <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416"
                      repeatCount="indefinite" />
                  </circle>
                </svg>
                <svg v-else class="download-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7,10 12,15 17,10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                一键解析
              </button>
            </form>

            <!-- <p class="hint-text">
              一个面向创作者的链接内容处理工具，适合二次创作、剪辑参考与本地保存
            </p> -->
          </div>

          <!-- 解析结果展示区域 -->
          <div v-if="showResult && parseResult" class="result-container">
            <div class="result-card">
              <!-- Header: Thumbnail + Title -->
              <div class="result-card-header">
                <div class="result-thumb">
                  <img :src="thumbnailBlobUrl || parseResult.thumbnail || (parseResult.thumbnails && parseResult.thumbnails[0]?.url)"
                    alt="Thumbnail" referrerpolicy="no-referrer" />
                </div>
                <div class="result-meta">
                  <h3 class="result-title">{{ parseResult.title || parseResult.description }}</h3>
                </div>
              </div>

              <!-- Tabs -->
              <div class="result-tabs">
                <button v-for="tab in availableTabs" :key="tab.id"
                  :class="['tab-btn', { active: activeTab === tab.id }]" @click="activeTab = tab.id">
                  {{ tab.label }}
                </button>
              </div>

              <!-- Content -->
              <div class="result-body">
                <!-- Video Tab -->
                <div v-if="activeTab === 'video'" class="tab-content">
                  <div class="download-table-header">
                    <span class="col-format">格式</span>
                    <span class="col-quality">质量</span>
                    <span class="col-size">大小</span>
                    <span class="col-action">功能</span>
                  </div>
                  <div class="download-list">
                    <template v-if="parseResult.formats && parseResult.formats.length > 0">
                      <div v-for="(item, index) in parseResult.formats" :key="'fmt' + index" class="download-row">
                        <span class="col-format">{{ item.ext || item.format_id }}</span>
                        <span class="col-quality">{{ item.resolution || item.quality || '未知' }}</span>
                        <span class="col-size">{{ formatSize(item.filesize) }}</span>
                        <div class="col-action">
                          <button class="action-btn download"
                            @click="downloadVideo(item.url, 'video', parseResult.http_headers)"
                            :disabled="downloadingItems.has(`video_${item.url}`)">
                            {{ downloadingItems.has(`video_${item.url}`) ? '下载...' : '下载' }}
                          </button>
                          <button class="action-btn copy" @click="copyLink(item.url, 'video')">复制链接</button>
                        </div>
                      </div>
                    </template>
                    <template v-else-if="parseResult.best_video">
                      <div class="download-row">
                        <span class="col-format">{{ parseResult.best_video.ext || 'mp4' }}</span>
                        <span class="col-quality">{{ parseResult.best_video.resolution || '未知' }}</span>
                        <span class="col-size">{{ formatSize(parseResult.best_video.filesize) }}</span>
                        <div class="col-action">
                          <button class="action-btn download"
                            @click="downloadVideo(parseResult.best_video.url, 'video', parseResult.http_headers)"
                            :disabled="downloadingItems.has(`video_${parseResult.best_video.url}`)">
                            {{ downloadingItems.has(`video_${parseResult.best_video.url}`) ? '下载...' : '下载' }}
                          </button>
                          <button class="action-btn copy" @click="copyLink(parseResult.best_video.url, 'video')">复制链接</button>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>

                <!-- Image Tab -->
                <div v-if="activeTab === 'image'" class="tab-content">
                  <div class="download-table-header cols-3">
                    <span class="col-format">格式</span>
                    <span class="col-quality">大小</span>
                    <span class="col-action">功能</span>
                  </div>
                  <div class="download-list">
                    <template v-if="parseResult.thumbnails && parseResult.thumbnails.length > 0">
                      <div v-for="(item, index) in parseResult.thumbnails" :key="'thumb' + index"
                        class="download-row cols-3">
                        <span class="col-format">jpg/webp</span>
                        <span class="col-quality">{{ item.width && item.height ? `${item.width}x${item.height}` : '未知'
                          }}</span>
                        <div class="col-action">
                          <button class="action-btn download"
                            @click="downloadVideo(item.url, 'cover', parseResult.http_headers)">下载</button>
                          <button class="action-btn copy" @click="copyLink(item.url, 'cover')">复制链接</button>
                        </div>
                      </div>
                    </template>
                    <template v-else-if="parseResult.thumbnail">
                      <div class="download-row cols-3">
                        <span class="col-format">jpg</span>
                        <span class="col-quality">未知</span>
                        <div class="col-action">
                          <button class="action-btn download"
                            @click="downloadVideo(parseResult.thumbnail, 'cover', parseResult.http_headers)">下载</button>
                          <button class="action-btn copy" @click="copyLink(parseResult.thumbnail, 'cover')">复制链接</button>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>

                <!-- Audio Tab -->
                <div v-if="activeTab === 'audio'" class="tab-content">
                  <div class="download-table-header cols-3">
                    <span class="col-format">格式</span>
                    <span class="col-size">大小</span>
                    <span class="col-action">功能</span>
                  </div>
                  <div class="download-list">
                    <template v-if="parseResult.audio_formats && parseResult.audio_formats.length > 0">
                      <div v-for="(item, index) in parseResult.audio_formats" :key="'aud' + index"
                        class="download-row cols-3">
                        <span class="col-format">{{ item.ext || 'mp3' }}</span>
                        <span class="col-size">{{ formatSize(item.filesize) }}</span>
                        <div class="col-action">
                          <button class="action-btn download"
                            @click="downloadVideo(item.url, 'audio', parseResult.http_headers)">下载</button>
                          <button class="action-btn copy" @click="copyLink(item.url, 'audio')">复制链接</button>
                        </div>
                      </div>
                    </template>
                    <template v-else-if="parseResult.best_audio">
                      <div class="download-row cols-3">
                        <span class="col-format">{{ parseResult.best_audio.ext || 'mp3' }}</span>
                        <span class="col-size">{{ formatSize(parseResult.best_audio.filesize) }}</span>
                        <div class="col-action">
                          <button class="action-btn download"
                            @click="downloadVideo(parseResult.best_audio.url, 'audio', parseResult.http_headers)">下载</button>
                          <button class="action-btn copy" @click="copyLink(parseResult.best_audio.url, 'audio')">复制链接</button>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>

              </div>
            </div>
          </div>

          <!-- Learn More -->
          </div>

          <div class="learn-more">
            <a href="#how-it-works" class="learn-more-link">
              <div class="learn-more-text">了解更多</div>
              <svg class="arrow-down" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="6,9 12,15 18,9"></polyline>
              </svg>
            </a>
          </div>
        </div>
      </section>

      <!-- How It Works Section -->
      <section id="how-it-works" class="how-it-works-section tool-info-section">
        <div class="container">
          <div class="tool-section-head">
            <h2 class="section-title">如何使用</h2>
            <p>三步完成解析，拿到你真正需要的素材内容。</p>
          </div>
          <div class="steps-grid">
            <div class="step-card">
              <div class="step-number">1</div>
              <h3 class="step-title">复制链接</h3>
              <p class="step-description">从短视频平台复制分享链接。</p>
            </div>
            <div class="step-card">
              <div class="step-number">2</div>
              <h3 class="step-title">粘贴链接</h3>
              <p class="step-description">粘贴到输入框并开始解析。</p>
            </div>
            <div class="step-card">
              <div class="step-number">3</div>
              <h3 class="step-title">下载视频</h3>
              <p class="step-description">按需下载视频、封面或音频。</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Features Section -->
      <section id="features" class="features-section tool-info-section">
        <div class="container">
          <div class="tool-section-head">
            <h2 class="section-title">功能特点</h2>
            <p>围绕下载、整理和复用场景，保留首页同一套简洁表达。</p>
          </div>
          <div class="features-grid">
            <div class="feature-card">
              <div class="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12,6 12,12 16,14"></polyline>
                </svg>
              </div>
              <h3 class="feature-title">快速处理</h3>
              <p class="feature-description">更快拿到解析结果与下载资源。</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M9 12l2 2 4-4"></path>
                  <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"></path>
                  <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3"></path>
                </svg>
              </div>
              <h3 class="feature-title">高清画质</h3>
              <p class="feature-description">尽量保留原始画质与清晰度。</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21,15 16,10 5,21"></polyline>
                </svg>
              </div>
              <h3 class="feature-title">多平台支持</h3>
              <p class="feature-description">覆盖常见短视频分享场景。</p>
            </div>
          </div>
        </div>
      </section>

      <!-- About Section -->
      <section id="about" class="about-section">
        <div class="container">
          <h2 class="section-title">关于我们</h2>
          <div class="about-content">
            <p class="about-description">
              我们致力于为用户提供最优质的视频处理服务。我们的去水印工具采用先进的技术，
              能够快速、高效地去除各种视频平台的水印，同时保持视频的原始画质。
            </p>
            <p class="about-description">
              我们支持国内外主流平台，让您轻松获取无水印的高清视频内容。
              我们承诺保护用户隐私，所有处理过程均在本地完成，不会上传或存储您的视频内容。
            </p>
          </div>
        </div>
      </section>
      </main>
    </div>

    <AppFooter />

    <!-- 错误弹窗 -->
    <div v-if="showErrorDialog" class="error-dialog-overlay" @click="closeErrorDialog">
      <div class="error-dialog" @click.stop>
        <div class="error-dialog-header">
          <h3 class="error-dialog-title">{{ errorDialogTitle }}</h3>
          <button @click="closeErrorDialog" class="error-dialog-close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="error-dialog-content">
          <div class="error-dialog-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
          </div>
          <p class="error-dialog-message">{{ errorDialogMessage }}</p>
        </div>
        <div class="error-dialog-footer">
          <button @click="closeErrorDialog" class="error-dialog-button">确定</button>
        </div>
      </div>
    </div>

    <!-- 预览弹窗 -->
    <div v-if="showPreviewDialog" class="preview-dialog-overlay" @click="closePreviewDialog">
      <div class="preview-dialog" @click.stop>
        <div class="preview-dialog-header">
          <h3 class="preview-dialog-title">预览</h3>
          <button @click="closePreviewDialog" class="preview-dialog-close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="preview-dialog-content">
          <div v-if="previewLoading" class="preview-loading">
            <svg class="loading-icon" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-dasharray="31.416" stroke-dashoffset="31.416">
                <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416"
                  repeatCount="indefinite" />
                <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416"
                  repeatCount="indefinite" />
              </circle>
            </svg>
            <span>加载预览中...</span>
          </div>
          <template v-else>
            <video v-if="previewType === 'video'" :src="previewUrl" controls autoplay controlsList="nodownload" class="preview-media"
              referrerpolicy="no-referrer"></video>
            <img v-if="previewType === 'image'" :src="previewUrl" class="preview-media" referrerpolicy="no-referrer" />
            <audio v-if="previewType === 'audio'" :src="previewUrl" controls autoplay controlsList="nodownload" @contextmenu.prevent class="preview-media-audio"
              referrerpolicy="no-referrer"></audio>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import request from '@/utils/request'
import { getParseKey } from '@/utils/crypto'
import { showDialog, showConfirmDialog, closeDialog } from 'vant'

export default {
  name: 'RemoveWatermark',
  components: { AppHeader, AppFooter },
  data() {
    return {
      videoUrl: '',
      isLoading: false,
      errorMessage: '',
      parseResult: null,
      showResult: false,
      activeTab: 'video',
      downloadingItems: new Set(), // 正在下载的项目
      downloadProgress: {}, // 下载进度
      showErrorDialog: false, // 显示错误弹窗
      errorDialogTitle: '', // 错误弹窗标题
      errorDialogMessage: '', // 错误弹窗内容
      showPreviewDialog: false, // 显示预览弹窗
      previewUrl: '', // 预览URL
      previewType: '', // 预览类型 (video, image, audio)
      previewLoading: false, // 预览加载状态
      isGoods: false, // 是否是商品
      thumbnailBlobUrl: '' // 缩略图Blob URL
    }
  },
  computed: {
    isDisabled() {
      return this.isLoading || !this.videoUrl.trim()
    },
    availableTabs() {
      if (!this.parseResult) return []
      const tabs = []

      // Video Tab
      if ((this.parseResult.formats && this.parseResult.formats.length > 0) || this.parseResult.best_video) {
        tabs.push({ id: 'video', label: '视频' })
      }

      // Image Tab
      if ((this.parseResult.thumbnails && this.parseResult.thumbnails.length > 0) || this.parseResult.thumbnail) {
        tabs.push({ id: 'image', label: '图片' })
      }

      // Audio Tab
      if ((this.parseResult.audio_formats && this.parseResult.audio_formats.length > 0) || this.parseResult.best_audio) {
        tabs.push({ id: 'audio', label: '音频' })
      }

      return tabs
    }
  },
  watch: {
    availableTabs: {
      handler(tabs) {
        if (tabs.length > 0 && !tabs.find(t => t.id === this.activeTab)) {
          this.activeTab = tabs[0].id
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.$refs.urlInput?.focus()
  },
  methods: {
    clearUrl() {
      this.videoUrl = ''
      this.errorMessage = ''
      this.showResult = false
      this.parseResult = null

      if (this.thumbnailBlobUrl && this.thumbnailBlobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.thumbnailBlobUrl)
      }
      this.thumbnailBlobUrl = ''

      this.$refs.urlInput?.focus()
    },

    formatSize(bytes) {
      if (bytes === null || bytes === undefined) return '未知';
      if (bytes === 0) return '0B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    async copyLink(url, type, forceUseStatus = 0) {
      const originalUrl = this.parseResult.original_url
      if (!url || !originalUrl) {
        this.showError('复制失败', '链接无效，请重新解析')
        return
      }
      try {
        // 检查是否是商品
        if (forceUseStatus == 0) {
          const checkGoodsRes = await request('/parse/check-goods', {
            method: 'POST',
            body: {
              parse_url: originalUrl
            }
          })
          this.isGoods = checkGoodsRes
        }

        let downloadType = 1
        if (type === 'cover') {
          downloadType = 2
        } else if (type === 'audio') {
          downloadType = 3
        }

        const checkRes = await request('/parse/check/download', {
          method: 'POST',
          body: {
            parse_url: originalUrl,
            type: downloadType,
            forceUseStatus
          }
        })
        console.log('checkRes:', checkRes)
        // Try using navigator.clipboard
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(() => {
            // You might want to show a toast here
            showDialog({ message: '链接已复制' })
          }).catch(err => {
            console.error('复制失败:', err)
            this.copyLinkFallback(url)
          })
        } else {
          this.copyLinkFallback(url)
        }
      }catch (error) {
        console.error('下载失败:', error)
        if (error.code == 4010) {
            showConfirmDialog({ message: '积分不足，成为会员后再尝试' })
            .then(() => { this.$router.push({ name: 'WebPay'}) })
            .catch(() => {})
        } else if(error.code == 407) { // 下载内容为商品
             showConfirmDialog({ message: '商品内容仅支持积分，继续将消耗1积分？' })
            .then(() => { this.copyLink(url, type, 1) })
            .catch(() => {})
        }
      }
        
    },

    copyLinkFallback(text) {
      try {
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.style.position = 'fixed'
        textarea.style.left = '-9999px'
        document.body.appendChild(textarea)
        textarea.select()

        const successful = document.execCommand('copy')
        document.body.removeChild(textarea)

        if (successful) {
          // alert('链接已复制')
          showDialog({ message: '链接已复制' })
        } else {
          console.error('复制失败: 无法执行复制命令')
          alert('复制失败，请手动复制')
        }
      } catch (err) {
        console.error('复制失败:', err)
        alert('复制失败，请手动复制')
      }
    },

    showError(title, message) {
      this.errorDialogTitle = title
      this.errorDialogMessage = message
      this.showErrorDialog = true
    },

    closeErrorDialog() {
      this.showErrorDialog = false
      this.errorDialogTitle = ''
      this.errorDialogMessage = ''
    },

    async loadThumbnail() {
      if (!this.parseResult) return

      const url = this.parseResult.thumbnail || (this.parseResult.thumbnails && this.parseResult.thumbnails[0]?.url)
      if (!url) return

      // 如果有旧的Blob URL，先清理
      if (this.thumbnailBlobUrl && this.thumbnailBlobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.thumbnailBlobUrl)
      }
      this.thumbnailBlobUrl = ''

      const httpHeaders = this.parseResult.http_headers
      if (!httpHeaders || Object.keys(httpHeaders).length === 0) {
        this.thumbnailBlobUrl = url
        return
      }

      try {
        const headers = { ...httpHeaders }
        let referrer = undefined
        const refererKey = Object.keys(headers).find(k => k.toLowerCase() === 'referer')
        if (refererKey) {
          referrer = headers[refererKey]
        }

        const fetchOptions = {
          method: 'GET',
          headers: headers,
          referrer: referrer,
          referrerPolicy: referrer ? 'no-referrer' : 'unsafe-url'
        }

        const response = await fetch(url, fetchOptions)
        if (!response.ok) {
           // 如果fetch失败，回退到原始URL
           console.warn(`Thumbnail fetch failed: ${response.status}, fallback to original url`)
           this.thumbnailBlobUrl = url
           return
        }

        const blob = await response.blob()
        this.thumbnailBlobUrl = URL.createObjectURL(blob)

      } catch (error) {
        console.error('加载缩略图失败:', error)
        this.thumbnailBlobUrl = url
      }
    },

    async previewContent(url, type, httpHeaders = null) {
      this.previewType = type
      this.showPreviewDialog = true

      // 如果没有header，直接使用URL
      if (!httpHeaders || Object.keys(httpHeaders).length === 0) {
        this.previewUrl = url
        return
      }

      // 如果有header，需要fetch获取blob
      this.previewLoading = true
      this.previewUrl = '' // 清除旧的URL

      try {
        const headers = { ...httpHeaders }
        let referrer = undefined
        const refererKey = Object.keys(headers).find(k => k.toLowerCase() === 'referer')
        if (refererKey) {
          referrer = headers[refererKey]
        }

        // 构造fetch选项，参考下载逻辑
        const fetchOptions = {
          method: 'GET',
          headers: headers,
          referrer: referrer,
          referrerPolicy: referrer ? 'no-referrer' : 'unsafe-url'
        }

        const response = await fetch(url, fetchOptions)

        if (!response.ok) {
          throw new Error(`Preview fetch failed: ${response.status}`)
        }

        const blob = await response.blob()
        this.previewUrl = URL.createObjectURL(blob)

      } catch (error) {
        console.error('预览加载失败:', error)
        this.showError('预览失败', '无法加载预览内容，请尝试直接下载')
        this.showPreviewDialog = false
      } finally {
        this.previewLoading = false
      }
    },

    closePreviewDialog() {
      this.showPreviewDialog = false
      // 清理Blob URL以释放内存
      if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.previewUrl)
      }
      this.previewUrl = ''
      this.previewType = ''
      this.previewLoading = false
    },

    getProgressText(type, url) {
      const downloadKey = `${type}_${url}`
      const progress = this.downloadProgress[downloadKey]
      if (!progress) return ''

      switch (progress.status) {
        case 'downloading':
          return '正在下载...'
        case 'processing':
          return '处理中...'
        case 'finalizing':
          return '准备完成...'
        case 'completed':
          return '下载完成！'
        case 'error':
          return '下载失败'
        default:
          return ''
      }
    },

    getDownloadButtonText(type, url) {
      const downloadKey = `${type}_${url}`
      const isDownloading = this.downloadingItems.has(downloadKey)
      const progress = this.downloadProgress[downloadKey]

      if (isDownloading && progress) {
        switch (progress.status) {
          case 'downloading':
            return '下载中...'
          case 'processing':
            return '处理中...'
          case 'finalizing':
            return '准备中...'
          case 'completed':
            return '已完成'
          case 'error':
            return '重试下载'
          default:
            return '下载中...'
        }
      }

      switch (type) {
        case 'video':
          return '下载视频'
        case 'audio':
          return '下载音频'
        case 'cover':
          return '下载封面'
        default:
          return '下载'
      }
    },

    // 根据URL获取合适的Referer
    getRefererFromUrl(url) {
      try {
        const urlObj = new URL(url)
        const hostname = urlObj.hostname.toLowerCase()

        // 根据不同平台设置不同的Referer
        if (hostname.includes('douyin') || hostname.includes('tiktok')) {
          return 'https://www.douyin.com/'
        } else if (hostname.includes('kuaishou')) {
          return 'https://www.kuaishou.com/'
        } else if (hostname.includes('xiaohongshu') || hostname.includes('xhscdn')) {
          return 'https://www.xiaohongshu.com/'
        } else if (hostname.includes('instagram') || hostname.includes('cdninstagram')) {
          return 'https://www.instagram.com/'
        } else if (hostname.includes('youtube') || hostname.includes('googlevideo')) {
          return 'https://www.youtube.com/'
        } else if (hostname.includes('twitter') || hostname.includes('twimg')) {
          return 'https://twitter.com/'
        } else {
          // 默认使用域名作为Referer
          return `${urlObj.protocol}//${urlObj.hostname}/`
        }
      } catch (error) {
        console.warn('无法解析URL，使用默认Referer:', error)
        return 'https://www.douyin.com/'
      }
    },

    // 检查服务器是否支持Range请求
    async checkRangeSupport(url, httpHeaders = null) {
      try {
        let headers = {}
        let referrer = undefined

        if (httpHeaders) {
          headers = { ...httpHeaders }
          const refererKey = Object.keys(headers).find(k => k.toLowerCase() === 'referer')
          if (refererKey) {
            referrer = headers[refererKey]
          }
        } else {
          headers = {
            'Accept': '*/*',
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
          }
          referrer = this.getRefererFromUrl(url)
        }

        const fetchOptions = {
          method: 'HEAD',
          headers: headers,
          referrer: referrer,
          referrerPolicy: referrer ? 'no-referrer' : 'unsafe-url'
        }

        const response = await fetch(url, fetchOptions)
        return response.headers.get('accept-ranges') === 'bytes'
      } catch {
        return false
      }
    },

    // 更新下载进度
    updateDownloadProgress(downloadKey, progress, speed = null, status = 'downloading') {
      this.downloadProgress[downloadKey] = {
        progress,
        status,
        speed: speed ? `${(speed / 1024 / 1024).toFixed(1)} MB/s` : null
      }
    },

    // 触发文件下载
    triggerDownload(blob, filename) {
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      link.style.display = 'none'

      document.body.appendChild(link)
      link.click()

      // 延迟清理
      setTimeout(() => {
        document.body.removeChild(link)
        window.URL.revokeObjectURL(downloadUrl)
      }, 1000)
    },

    // 多线程下载实现
    async downloadVideoWithMultiThread(url, type, chunkSize = 1024 * 1024, httpHeaders = null) {
      const downloadKey = `${type}_${url}`

      try {
        // 1. 获取文件总大小
        let headers = {}
        let referrer = undefined

        if (httpHeaders) {
          headers = { ...httpHeaders }
          const refererKey = Object.keys(headers).find(k => k.toLowerCase() === 'referer')
          if (refererKey) {
            referrer = headers[refererKey]
          }
        } else {
          headers = {
            'Accept': '*/*',
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
          }
          referrer = this.getRefererFromUrl(url)
        }

        const headFetchOptions = {
          method: 'HEAD',
          headers: headers,
          referrer: referrer,
          referrerPolicy: referrer ? 'no-referrer' : 'unsafe-url'
        }

        const headResponse = await fetch(url, headFetchOptions)
        const fileSize = parseInt(headResponse.headers.get('content-length'))

        if (!fileSize || fileSize < chunkSize * 2) {
          // 如果无法获取文件大小或文件太小，回退到单线程下载
          return this.downloadVideoSingleThread(url, type, httpHeaders)
        }

        // 2. 计算分片数量和并发数
        const chunkCount = Math.ceil(fileSize / chunkSize)
        const maxConcurrent = Math.min(chunkCount, 6) // 最多6个并发连接

        console.log(`开始多线程下载: 文件大小 ${(fileSize / 1024 / 1024).toFixed(1)}MB, 分片数 ${chunkCount}, 并发数 ${maxConcurrent}`)

        // 3. 初始化下载状态
        this.updateDownloadProgress(downloadKey, 0, null, 'downloading')

        // 4. 创建下载任务
        const chunks = new Array(chunkCount)
        const downloadedChunks = new Set()
        const startTime = Date.now()
        let downloadedBytes = 0

        // 5. 分批下载
        for (let batchStart = 0; batchStart < chunkCount; batchStart += maxConcurrent) {
          const batchEnd = Math.min(batchStart + maxConcurrent, chunkCount)
          const batchPromises = []

          for (let i = batchStart; i < batchEnd; i++) {
            const start = i * chunkSize
            const end = Math.min(start + chunkSize - 1, fileSize - 1)

            const downloadChunk = async () => {
              const chunkHeaders = { ...headers, 'Range': `bytes=${start}-${end}` }
              const chunkFetchOptions = {
                headers: chunkHeaders,
                referrer: referrer,
                referrerPolicy: referrer ? 'no-referrer' : 'unsafe-url'
              }
              const response = await fetch(url, chunkFetchOptions)

              if (!response.ok) {
                throw new Error(`分片下载失败: ${response.status}`)
              }

              const arrayBuffer = await response.arrayBuffer()
              chunks[i] = new Uint8Array(arrayBuffer)
              downloadedChunks.add(i)
              downloadedBytes += arrayBuffer.byteLength

              // 计算下载速度和进度
              const elapsed = (Date.now() - startTime) / 1000
              const speed = downloadedBytes / elapsed
              const progress = Math.round((downloadedChunks.size / chunkCount) * 100)

              this.updateDownloadProgress(downloadKey, progress, speed)
            }

            batchPromises.push(downloadChunk())
          }

          // 等待当前批次完成
          await Promise.all(batchPromises)
        }

        // 6. 合并所有片段
        this.updateDownloadProgress(downloadKey, 95, null, 'finalizing')

        const mergedArray = new Uint8Array(fileSize)
        let offset = 0
        for (const chunk of chunks) {
          if (chunk) {
            mergedArray.set(chunk, offset)
            offset += chunk.length
          }
        }

        // 7. 创建blob并下载
        const blob = new Blob([mergedArray])

        // 生成文件名
        const timestamp = new Date().getTime().toString().slice(-8)
        let filename = `download-${timestamp}`

        switch (type) {
          case 'video':
            filename += '.mp4'
            break
          case 'audio':
            filename += '.mp3'
            break
          case 'cover':
            filename += '.jpg'
            break
          default:
            filename += '.mp4'
        }

        this.triggerDownload(blob, filename)

        // 8. 完成状态
        this.updateDownloadProgress(downloadKey, 100, null, 'completed')

        // 延迟清理
        setTimeout(() => {
          this.downloadingItems.delete(downloadKey)
          delete this.downloadProgress[downloadKey]
        }, 2000)

        console.log('多线程下载完成')

      } catch (error) {
        console.error('多线程下载失败，回退到单线程:', error)
        return this.downloadVideoSingleThread(url, type, httpHeaders)
      }
    },

    // 单线程下载（原有实现）
    async downloadVideoSingleThread(url, type, httpHeaders = null) {
      const downloadKey = `${type}_${url}`

      try {
        // 生成文件名
        const timestamp = new Date().getTime().toString().slice(-8)
        let filename = `download-${timestamp}`

        switch (type) {
          case 'video':
            filename += '.mp4'
            break
          case 'audio':
            filename += '.mp3'
            break
          case 'cover':
            filename += '.jpg'
            break
          default:
            filename += '.mp4'
        }

        // 使用fetch下载文件，添加超时控制
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 300000) // 5分钟超时

        let headers = {}
        let referrer = undefined

        if (httpHeaders) {
          headers = { ...httpHeaders }
          const refererKey = Object.keys(headers).find(k => k.toLowerCase() === 'referer')
          if (refererKey) {
            referrer = headers[refererKey]
          }
        } else {
          headers = {
            'Accept': '*/*',
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
          }
          referrer = this.getRefererFromUrl(url)
        }

        const fetchOptions = {
          method: 'GET',
          headers: headers,
          signal: controller.signal,
          referrer: referrer,
          referrerPolicy: referrer ? 'no-referrer' : 'unsafe-url'
        }

        const response = await fetch(url, fetchOptions)

        clearTimeout(timeoutId)

        if (!response.ok) {
          throw new Error(`下载失败: HTTP ${response.status}`)
        }

        // 检查文件大小
        const contentLength = response.headers.get('content-length')
        if (contentLength) {
          const fileSize = parseInt(contentLength, 10)
          const fileSizeMB = fileSize / (1024 * 1024)

          // 如果文件大于100MB，给出警告但继续下载
          if (fileSizeMB > 100) {
            console.warn(`文件较大: ${fileSizeMB.toFixed(1)}MB，下载可能需要较长时间`)
          }
        }

        // 更新进度
        this.updateDownloadProgress(downloadKey, 50, null, 'processing')

        // 获取文件blob
        const blob = await response.blob()

        // 更新进度
        this.updateDownloadProgress(downloadKey, 80, null, 'finalizing')

        // 触发下载
        this.triggerDownload(blob, filename)

        // 完成状态
        this.updateDownloadProgress(downloadKey, 100, null, 'completed')

        // 延迟清理
        setTimeout(() => {
          this.downloadingItems.delete(downloadKey)
          delete this.downloadProgress[downloadKey]
        }, 2000)

      } catch (error) {
        console.error('单线程下载失败:', error)
        if (error.message) {
          console.log('error msg:', error.message)
        }

        // 设置错误状态
        this.downloadProgress[downloadKey] = { progress: 0, status: 'error' }

        // 根据错误类型给出不同提示
        if (error.name === 'AbortError') {
          this.showError('下载超时', '请检查网络连接或稍后重试')
        } else if ((error.message && error.message.includes('HTTP') && error.message.includes('403')) || (error.message && error.message.includes('Failed to fetch'))) {
          showConfirmDialog({
            title: '下载提示',
            message: '因浏览器安全限制，无法直接下载。\n可以使用云端进行下载，但耗时较长是否继续？',
            confirmButtonText: '继续下载',
            showCancelButton: true
          }).then(() => {
            this.handleProxyDownload(url, type, httpHeaders)
          }).catch(() => {})
        } else if (error.message && error.message.includes('HTTP')) {
          console.error(error.message)
          this.showError('下载失败', '下载链接无效或已过期，请重新解析视频')
        } else {
          // 其他错误（如CORS）尝试在新标签页打开
          showConfirmDialog({
            title: '下载提示',
            message: '由于浏览器安全限制，无法直接下载。\n点击“我知道了”将在新标签页打开视频，请在视频页面右键点击"视频另存为"进行保存。',
            confirmButtonText: '我知道了',
            showCancelButton: false
          }).then(() => {
            window.open(url, '_blank')
          }).catch(() => {})
        }

        // 清理错误状态
        setTimeout(() => {
          this.downloadingItems.delete(downloadKey)
          delete this.downloadProgress[downloadKey]
        }, 3000)
      }
    },

    async downloadVideo(url, type, httpHeaders = null, forceUseStatus = 0) {
      console.log('开始下载:', { url, type, httpHeaders })

      const originalUrl = this.parseResult.original_url
      if (!url || !originalUrl) {
        this.showError('下载失败', '下载链接无效，请重新解析视频')
        return
      }

      // 防止重复下载
      const downloadKey = `${type}_${url}`
      if (this.downloadingItems.has(downloadKey)) {
        return
      }

      // 开始下载状态
      this.downloadingItems.add(downloadKey)
      this.updateDownloadProgress(downloadKey, 0)
      this.errorMessage = ''

      try {
        // 检查是否是商品
        if (forceUseStatus == 0) {
          const checkGoodsRes = await request('/parse/check-goods', {
            method: 'POST',
            body: {
              parse_url: originalUrl
            }
          })
          this.isGoods = checkGoodsRes
        }

        let downloadType = 1
        if (type === 'cover') {
          downloadType = 2
        } else if (type === 'audio') {
          downloadType = 3
        }

        const checkRes = await request('/parse/check/download', {
          method: 'POST',
          body: {
            parse_url: originalUrl,
            type: downloadType,
            forceUseStatus
          }
        })
        console.log('checkRes:', checkRes)

        if (type == 'cover') {
          // 检查是否支持Range请求
          const supportsRange = await this.checkRangeSupport(url, httpHeaders)

          if (supportsRange) {
            console.log('服务器支持Range请求，使用多线程下载')
            await this.downloadVideoWithMultiThread(url, type, 1024 * 1024, httpHeaders)
          } else {
            console.log('服务器不支持Range请求，使用单线程下载')
            await this.downloadVideoSingleThread(url, type, httpHeaders)
          }
        } else {
            // 直接打开浏览器标签页
            window.open(url, '_blank')
            this.downloadingItems.delete(downloadKey)
        }
      } catch (error) {
        console.error('下载失败:', error)
        if (error.code == 4010) {
            showConfirmDialog({ message: '积分不足，成为会员后再尝试' })
            .then(() => { this.$router.push({ name: 'WebPay'}) })
            .catch(() => {})
        } else if(error.code == 407) { // 下载内容为商品
             showConfirmDialog({ message: '商品内容仅支持积分，继续将消耗1积分？' })
            .then(() => { this.downloadVideo(url, type, httpHeaders, 1) })
            .catch(() => {})
        }
        // 设置错误状态
        this.downloadProgress[downloadKey] = { progress: 0, status: 'error' }

        this.showError('下载失败', '下载过程中出现错误，请稍后重试')

        // 清理错误状态
        setTimeout(() => {
          this.downloadingItems.delete(downloadKey)
          delete this.downloadProgress[downloadKey]
        }, 3000)
      }
    },

    async handleSubmit() {
      const token = localStorage.getItem('token')
      const session = localStorage.getItem('session')
      if (!token || !session) {
        console.log('token失效或不可用')
        window.dispatchEvent(new CustomEvent('auth-login-required', { detail: { msg: '登录失效或不可用，请重新登录' } }))
        return
      }
      this.errorMessage = ''
      this.showResult = false

      // 基本URL验证
      if (!this.videoUrl.trim()) {
        this.errorMessage = '请输入视频链接'
        return
      }

      this.isLoading = true

      try {
        // 调用视频解析API
        // const response = await parseVideo(this.videoUrl)

        const checkRes = await request('/parse/check', {
          method: 'POST'
        })
        console.log('checkRes:', checkRes)
        const sign = getParseKey(checkRes, session)

        const remRes = await request('/parse/rem-url', {
          method: 'POST',
          body: {
            parse_url: this.videoUrl,
            key: sign
          }
        })
        this.parseResult = remRes
        this.showResult = true
        this.errorMessage = ''

        // 加载缩略图
        this.loadThumbnail()

      } catch (error) {
        console.error('解析视频失败:', error)
        showDialog({ message: error.message || '背景移除解析失败，请检查链接是否正确或稍后重试失败' })
      } finally {
        this.isLoading = false
      }
    },

    async handleProxyDownload(url, type, httpHeaders) {
      try {
        showDialog({ message: '正在进行云端下载，请稍候...（大文件可能需要较长时间）', showConfirmButton: false })

        const data = await request('/media/proxy/download', {
          method: 'POST',
          contentType: 'application/json',
          body: {
            url: url,
            headers: httpHeaders
          }
        })

        closeDialog()

        showConfirmDialog({ message: '云端处理完成，开始下载？', confirmButtonText: '确定' })
        .then(() => { this.downloadVideoSingleThread(data.url, type, null) })
        .catch(() => {})

      } catch (error) {
        closeDialog()
        console.error('云端下载出错:', error)
        showConfirmDialog({ 
          message: '云端下载失败: ' + (error.message || '未知错误'),
          confirmButtonText: '确定'
        }).catch(() => {})
      }
    }
  }
}
</script>

<style scoped>
.home-page {
  --primary-color: #4f46e5;
  --primary-hover: #4338ca;
  --bg-color: #f9fafb;
  --card-bg: #ffffff;
  --text-main: #111827;
  --text-muted: #6b7280;
  --border-color: #e5e7eb;
}

.dark .home-page {
  --primary-color: #6366f1;
  --primary-hover: #818cf8;
  --bg-color: #111827;
  --card-bg: #1f2937;
  --text-main: #f9fafb;
  --text-muted: #9ca3af;
  --border-color: #374151;
}

.home-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--bg-color);
  color: var(--text-main);
  text-align: left;
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

/* Header Styles */
.home-header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border-color);
  transition: all 0.3s ease;
}

.dark .home-header {
  background: rgba(17, 24, 39, 0.8);
}

.home-header-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.home-logo {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.home-logo-image {
  height: 2.5rem;
  display: block;
}

.home-nav {
  display: none;
  gap: 2rem;
  margin-left: 2rem;
  flex: 1;
}

@media (min-width: 768px) {
  .home-nav {
    display: flex;
    align-items: center;
  }
}

.home-nav-item {
  font-size: 0.95rem;
  color: var(--text-muted);
  text-decoration: none;
  position: relative;
  font-weight: 500;
  transition: color 0.2s;
}

.home-nav-item.router-link-exact-active,
.home-nav-item:hover {
  color: var(--primary-color);
}

.home-header-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.home-user-button {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 999px;
  border: 1px solid var(--border-color);
  background: var(--card-bg);
  color: var(--text-main);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.home-user-button:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.home-user-button svg {
  width: 1.4rem;
  height: 1.4rem;
  stroke-width: 1.8;
}

.home-gradient {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 50% 0%, rgba(79, 70, 229, 0.1) 0%, transparent 50%);
  pointer-events: none;
  z-index: 0;
}

.home-main {
  flex: 1;
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4rem;
  position: relative;
  z-index: 1;
}

/* Hero Section */
.hero-section {
  padding: 4rem 1rem 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.tool-page .hero-section.tool-hero-section {
  padding: 28px;
}

.hero-content {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-align: left;
  max-width: none;
  margin: 0;
}

.tool-page .hero-content {
  width: 100%;
}

.hero-title {
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #111827 0%, #4f46e5 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.dark .hero-title {
  background: linear-gradient(135deg, #f9fafb 0%, #818cf8 100%);
  -webkit-background-clip: text;
  background-clip: text;
}

.hero-description {
  font-size: 1.1rem;
  color: var(--text-muted);
  max-width: 760px;
  margin: 0 0 2rem;
  line-height: 1.6;
}

/* Footer */
.home-footer {
  margin-top: auto;
  background: var(--card-bg);
  color: var(--text-muted);
  padding: 2rem 0;
  border-top: 1px solid var(--border-color);
}

.home-footer-item a {
  color: inherit;
  text-decoration: none;
}

.home-footer-item a:hover {
  text-decoration: underline;
}

.home-footer-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.875rem;
}

/* Form Styles */
.video-form-container {
  width: 100%;
  max-width: none;
  margin: 24px 0 0;
  /* margin: 30px auto 0 auto; */
}

.tool-page .video-form-container {
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.84);
  border-radius: 24px;
  box-shadow: 0 18px 45px rgba(50, 71, 117, 0.08);
  padding: 24px;
}

.tool-page .result-container {
  width: 100%;
}

.input-wrapper {
  position: relative;
  width: 100%;
  transition: transform 0.2s;
}

.url-input {
  width: 100%;
  box-sizing: border-box;
  padding: 1.25rem 3rem 1.25rem 1.5rem;
  border: 2px solid var(--border-color);
  border-radius: 1rem;
  font-size: 1.1rem;
  background: var(--card-bg);
  color: var(--text-main);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.url-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 10px 15px -3px rgba(79, 70, 229, 0.1), 0 4px 6px -2px rgba(79, 70, 229, 0.05);
  transform: translateY(-2px);
}

.submit-button {
  margin-top: 1.5rem;
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-hover));
  color: white;
  border: none;
  border-radius: 1rem;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 6px -1px rgba(79, 70, 229, 0.3);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(79, 70, 229, 0.4);
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  background: var(--text-muted);
  box-shadow: none;
}

.clear-button {
  position: absolute;
  top: 50%;
  right: 1rem;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  color: var(--text-muted);
  transition: color 0.2s;
}

.clear-button:hover {
  color: #ef4444;
}

.clear-icon {
  width: 1.25rem;
  height: 1.25rem;
}

.loading-icon,
.download-icon {
  width: 1.25rem;
  height: 1.25rem;
}

/* Result Card Styles */
.result-card {
  background: var(--card-bg);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  color: var(--text-main);
  margin-top: 2rem;
  border: 1px solid var(--border-color);
}

.result-card-header {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-color);
}

.result-thumb {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid var(--border-color);
}

.result-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.result-meta {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.result-title {
  font-size: 1rem;
  line-height: 1.5;
  font-weight: 500;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: var(--text-main);
}

.result-tabs {
  display: flex;
  border-bottom: 1px solid var(--border-color);
  padding: 0 1.5rem;
  background: var(--card-bg);
}

.tab-btn {
  padding: 1rem 1.5rem;
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
}

.tab-btn:hover {
  color: var(--text-main);
}

.tab-btn.active {
  color: var(--primary-color);
}

.tab-btn.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: var(--primary-color);
}

.result-body {
  padding: 1.5rem;
  background: var(--card-bg);
}

.download-table-header {
  display: grid;
  grid-template-columns: 100px 120px 100px 1fr;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-muted);
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.download-table-header.cols-3 {
  grid-template-columns: 100px 220px 1fr;
}

.download-list {
  display: flex;
  flex-direction: column;
}

.download-row {
  display: grid;
  grid-template-columns: 100px 120px 100px 1fr;
  padding: 1rem 0;
  border-bottom: 1px solid var(--border-color);
  align-items: center;
  font-size: 0.9rem;
}

.download-row.cols-3 {
  grid-template-columns: 100px 220px 1fr;
}

.download-row:last-child {
  border-bottom: none;
}

.col-format {
  font-weight: 600;
  color: var(--text-main);
}

.col-quality,
.col-size {
  color: var(--text-muted);
}

.col-action {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

.action-btn {
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  border: none;
  font-weight: 500;
}

.action-btn.download {
  background: var(--primary-color);
  color: white;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.action-btn.download:hover {
  background: var(--primary-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.action-btn.download:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: var(--text-muted);
  box-shadow: none;
  transform: none;
}

.action-btn.copy {
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-main);
}

.action-btn.copy:hover {
  border-color: var(--text-main);
  background: var(--bg-color);
}

.action-btn.preview {
  background: var(--text-muted);
  color: white;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.action-btn.preview:hover {
  background: var(--text-main);
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Mobile Responsiveness for Table */
@media (max-width: 640px) {
  .download-table-header {
    display: none;
  }

  .download-row,
  .download-row.cols-3 {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    padding: 1rem;
    background: var(--bg-color);
    margin-bottom: 0.5rem;
    border-radius: 8px;
    border: 1px solid var(--border-color);
  }

  .col-format {
    font-size: 1.1rem;
    margin-bottom: 0.25rem;
  }

  .col-quality::before {
    content: 'Quality: ';
    color: var(--text-muted);
  }

  .col-size::before {
    content: 'Size: ';
    color: var(--text-muted);
  }

  .col-action {
    margin-top: 0.5rem;
    justify-content: flex-start;
  }
}

/* Features & Steps */
.section-title {
  font-size: 2.25rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 4rem;
  color: var(--text-main);
}

.steps-grid,
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  padding: 0 1rem;
}

.step-card,
.feature-card {
  background: var(--card-bg);
  padding: 2.5rem 2rem;
  border-radius: 1.5rem;
  text-align: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
}

.step-card:hover,
.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  border-color: var(--primary-color);
}

.step-number {
  width: 3.5rem;
  height: 3.5rem;
  background: var(--primary-color);
  color: white;
  font-size: 1.5rem;
  margin: 0 auto 1.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  box-shadow: 0 10px 15px -3px rgba(79, 70, 229, 0.3);
}

.feature-icon {
  color: var(--primary-color);
  background: rgba(79, 70, 229, 0.1);
  width: 4rem;
  height: 4rem;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}

.feature-icon svg {
  width: 2rem;
  height: 2rem;
}

.step-title,
.feature-title {
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: var(--text-main);
}

.step-description,
.feature-description {
  color: var(--text-muted);
  line-height: 1.6;
}

/* About Section */
.about-section {
  padding: 4rem 1rem;
  background: transparent;
  text-align: center;
}

.about-content {
  max-width: 800px;
  margin: 0 auto;
}

.about-description {
  font-size: 1.1rem;
  line-height: 1.8;
  color: var(--text-muted);
  margin-bottom: 1.5rem;
}

/* Learn More */
.learn-more {
  margin-top: 2rem;
  text-align: center;
}

.learn-more-link {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  color: var(--text-muted);
  text-decoration: none;
  transition: color 0.2s;
}

.learn-more-link:hover {
  color: var(--primary-color);
}

.learn-more-text {
  font-size: 0.9rem;
  margin-bottom: 0;
}

.arrow-down {
  width: 1.5rem;
  height: 1.5rem;
  animation: bounce 2s infinite;
}

@keyframes bounce {

  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }

  40% {
    transform: translateY(-10px);
  }

  60% {
    transform: translateY(-5px);
  }
}

/* Error Dialog */
.error-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.error-dialog {
  background: var(--card-bg);
  border-radius: 1rem;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.error-dialog-header {
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--border-color);
}

.error-dialog-title {
  font-weight: 600;
  font-size: 1.1rem;
  color: var(--text-main);
}

.error-dialog-close {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted);
}

.error-dialog-content {
  padding: 1.5rem;
  display: flex;
  gap: 1rem;
}

.error-dialog-icon {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: #fee2e2;
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.error-dialog-message {
  color: var(--text-main);
  line-height: 1.5;
}

.error-dialog-footer {
  padding: 1rem 1.5rem;
  background: var(--bg-color);
  display: flex;
  justify-content: flex-end;
}

.error-dialog-button {
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  cursor: pointer;
  font-weight: 500;
}

.error-dialog-button:hover {
  background: #dc2626;
}

/* Responsive */
@media (max-width: 900px) {
  .home-header-inner {
    padding: 0.75rem 1rem;
  }

  .home-nav {
    display: none;
  }

  .home-footer-inner {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
}

@media (max-width: 640px) {
  .hero-title {
    font-size: 2rem;
  }

  .download-item {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .download-btn {
    width: 100%;
    justify-content: center;
  }

  .hero-section {
    padding-top: 4rem;
  }
}

.hint-text {
  margin-top: 1rem;
  font-size: 0.875rem;
  color: var(--text-muted);
}

/* Preview Dialog */
.preview-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  backdrop-filter: blur(4px);
}

.preview-dialog {
  background: var(--card-bg);
  border-radius: 1rem;
  width: 90%;
  max-width: 800px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.preview-dialog-header {
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-color);
}

.preview-dialog-title {
  font-weight: 600;
  font-size: 1.1rem;
  color: var(--text-main);
  margin: 0;
}

.preview-dialog-close {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted);
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  border-radius: 0.5rem;
}

.preview-dialog-close:hover {
  color: var(--text-main);
  background: rgba(0, 0, 0, 0.05);
}

.preview-dialog-close svg {
  width: 1.5rem;
  height: 1.5rem;
}

.preview-dialog-content {
  padding: 0;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  overflow: hidden;
  min-height: 200px;
  position: relative;
}

.preview-media {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

.preview-media-audio {
  width: 100%;
  padding: 2rem;
  background: var(--card-bg);
}

.preview-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: #fff;
}

.preview-loading .loading-icon {
  width: 3rem;
  height: 3rem;
}

.tool-page .home-main {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0;
}

.tool-page .hero-section.tool-page-hero {
  padding: 28px;
}

.tool-page .tool-hero-copy {
  text-align: left;
  align-items: flex-start;
}

.tool-page .tool-hero-copy .hero-title,
.tool-page .tool-hero-copy .hero-description {
  text-align: left;
}

.tool-page .tool-workbench--link {
  padding: 0;
  background: transparent;
  border: 0;
  box-shadow: none;
}

.tool-page .hero-content,
.tool-page .hero-text,
.tool-page .container,
.tool-page .video-form-container,
.tool-page .result-container,
.tool-page .about-content {
  max-width: none !important;
  width: 100%;
  margin-left: 0;
  margin-right: 0;
}

.tool-page .video-form-container {
  padding: 28px;
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 18px 45px rgba(50, 71, 117, 0.08);
}

.tool-page .result-container {
  margin-top: 20px;
}

.tool-page .learn-more {
  display: flex;
  justify-content: center;
}

.tool-page .tool-info-section .container {
  width: 100%;
}

.tool-page .section-title {
  margin-bottom: 0;
}

.tool-page .about-section {
  padding: 28px;
  background: var(--panel);
  border: 1px solid rgba(255, 255, 255, 0.7);
  box-shadow: var(--shadow);
  border-radius: var(--radius-lg);
}

.tool-page .about-content {
  display: grid;
  gap: 12px;
}

.tool-page .about-description {
  margin: 0;
  color: var(--muted);
  line-height: 1.9;
}

@media (max-width: 900px) {
  .tool-page .hero-section.tool-page-hero {
    padding: 20px;
  }

  .tool-page .tool-workbench--link,
  .tool-page .about-section {
    padding: 20px;
  }

  .tool-page .video-form-container {
    padding: 20px;
  }
}
</style>
